import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Coins, Save, Download, Loader2, Lock, Unlock, Square, LayoutGrid, ZoomIn, ZoomOut, Maximize2, Home, Pencil, Check, X as XIcon, Undo2, Redo2 } from 'lucide-react';
import { SEO } from '../../../shared/components';
import { FloorPlanEditor, FPLayout } from '../components/FloorPlanEditor';
import { BoundaryFigureEditor } from '../components/BoundaryFigureEditor';
import { House2Editor } from '../components/House2Editor';
import { buildHousePreset, geomFromCanvas } from '../components/housePresets';
import { useAuth } from '../../auth';
import { config } from '../../auth/config';
import { route } from '../../../shared/utils';

async function apiFetch(method: string, endpoint: string, token: string, body?: object) {
  const res = await fetch(`${config.api.baseUrl}${endpoint}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      [config.auth.headers.frontendKey]: config.auth.headers.frontendKeyValue,
      [config.auth.headers.auth]: `Bearer ${token}`,
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    console.error(`[apiFetch] ${method} ${endpoint} → ${res.status}`, text);
    return null;
  }
  return res.json();
}

// Compact toolbar button used in the custom flex header.
const FxBtn: React.FC<{
  active?: boolean; disabled?: boolean; title: string; onClick: () => void;
  compact?: boolean; children: React.ReactNode;
}> = ({ active, disabled, title, onClick, compact, children }) => (
  <button
    title={title}
    onClick={onClick}
    disabled={disabled}
    style={{
      display: 'flex', alignItems: 'center', gap: 5,
      background: active ? '#FFD93D' : '#1f1f1f',
      color: active ? '#1a1a1a' : '#ddd',
      border: '1px solid ' + (active ? '#FFD93D' : '#333'),
      borderRadius: 8, padding: compact ? '7px' : '6px 11px',
      fontSize: 12, fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.4 : 1, whiteSpace: 'nowrap', lineHeight: 1,
    }}
  >
    {children}
  </button>
);

const SERVICE_NAMES: Record<string, string> = {
  plot_regular: 'Regular Plot',
  plot_regular_room: 'Regular Plot + Room',
  plot_irregular: 'Irregular Plot',
  plot_irregular_room: 'Irregular Plot + Room',
};

// Cache buster for the iframe — stable per page load (dev: timestamp,
// prod: bumped on each deploy via script-embed asset hash isn't possible
// since it's served as a static legacy file, so we use the build mode).
const IFRAME_CACHE_KEY = import.meta.env.DEV ? String(Date.now()) : 'r22';

function useIsMobile(breakpoint = 1024) {
  const [mobile, setMobile] = useState(() => window.innerWidth <= breakpoint);
  useEffect(() => {
    const h = () => setMobile(window.innerWidth <= breakpoint);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, [breakpoint]);
  return mobile;
}

const FlexPage: React.FC = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const isMobile = useIsMobile();

  const [serviceCode, setServiceCode] = useState(searchParams.get('service') || 'plot_regular');
  // House preset chosen on the template picker (?preset=…) — applied once on load.
  const [pendingPreset, setPendingPreset] = useState(() => searchParams.get('preset') || '');
  const contentMode = searchParams.get('content') || 'sample';
  const designId = searchParams.get('designId');

  const [balance, setBalance] = useState<number | null>(null);
  const [iframeReady, setIframeReady] = useState(false);
  // Keep the iframe covered until the preset/design is actually applied, so the
  // user never sees the sample content flash to the real layout on first load.
  const [settled, setSettled] = useState(() => !(searchParams.get('preset') || searchParams.get('designId')));
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [showDownloadDialog, setShowDownloadDialog] = useState(false);
  const [downloadCost, setDownloadCost] = useState(0);
  const [isReDownload, setIsReDownload] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [currentDesignId, setCurrentDesignId] = useState<string | null>(designId);
  const defaultName = `${SERVICE_NAMES[serviceCode] || 'Plot'} #${Date.now().toString(36).slice(-4).toUpperCase()}`;
  const [currentPlotName, setCurrentPlotName] = useState(defaultName);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [currentFormData, setCurrentFormData] = useState<any>(null);
  // Live form data + a pending GET_FORM_DATA resolver, so save always reads the
  // latest engine state (canvas moves don't fire input events / FORM_CHANGED).
  const formDataRef = useRef<any>(null);
  const pendingFormResolve = useRef<((d: any) => void) | null>(null);
  // Mobile always shows the plot preview; editing is via the mode switcher + editors.
  const mobileTab = 'preview' as const;

  // Flex editor state mirrored from the iframe (drives the custom header tools)
  const [flexLocked, setFlexLocked] = useState(false);
  const [flexMode, setFlexMode] = useState<'rectangle' | 'trapezoid'>('rectangle');
  const [flexRoom, setFlexRoom] = useState(false);
  const [uiMode, setUiMode] = useState<'boundaries' | 'roads' | 'house' | 'canvas' | 'boundary' | 'house2'>('canvas');
  const [roads, setRoads] = useState({ N: false, S: false, E: false, W: false });
  const [showBoundaryFig, setShowBoundaryFig] = useState(false);
  const [showHouse2, setShowHouse2] = useState(false);
  const [fitted, setFitted] = useState(false); // fit-to-screen toggle (off = normal zoom)
  const sendFlexCmd = useCallback((cmd: string, extra?: Record<string, unknown>) => {
    iframeRef.current?.contentWindow?.postMessage({ type: 'FLEX_CMD', cmd, ...(extra || {}) }, '*');
  }, []);
  // Post a bare message (undo/redo) to the iframe engine.
  const sendMsg = useCallback((type: string) => {
    iframeRef.current?.contentWindow?.postMessage({ type }, '*');
  }, []);

  // Rename the plot (header pencil) — update the label and sync the iframe's
  // plotName so it persists with the design.
  const saveName = useCallback((val: string) => {
    const v = val.trim();
    if (v) {
      setCurrentPlotName(v);
      if (formDataRef.current) formDataRef.current = { ...formDataRef.current, plotName: v };
      iframeRef.current?.contentWindow?.postMessage({ type: 'SET_FIELDS', fields: { plotName: v } }, '*');
    }
    setEditingName(false);
  }, []);

  // Floor-plan (multi-room house) editor modal
  const [showFloorPlan, setShowFloorPlan] = useState(false);
  // Undo/redo via keyboard for the Document canvas. Editor modals (House/Plot)
  // handle their own, so skip while one is open.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (showHouse2 || showBoundaryFig || showFloorPlan) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      const meta = e.ctrlKey || e.metaKey;
      if (meta && (e.key === 'z' || e.key === 'Z')) { e.preventDefault(); sendMsg(e.shiftKey ? 'REDO' : 'UNDO'); }
      else if (meta && (e.key === 'y' || e.key === 'Y')) { e.preventDefault(); sendMsg('REDO'); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [sendMsg, showHouse2, showBoundaryFig, showFloorPlan]);
  const [floorPlanInitial, setFloorPlanInitial] = useState<FPLayout | null>(null);
  const [floorPlanAspect, setFloorPlanAspect] = useState(1.35);

  // Handle payment return query params
  const [paymentBanner, setPaymentBanner] = useState<string | null>(null);
  useEffect(() => {
    const p = searchParams.get('payment');
    if (p === 'success') {
      const credits = searchParams.get('credits') || '0';
      setPaymentBanner(`Payment successful! ₹${credits} credits added.`);
      // Clean up URL
      const next = new URLSearchParams(searchParams);
      next.delete('payment');
      next.delete('credits');
      setSearchParams(next, { replace: true });
    } else if (p === 'failed') {
      setPaymentBanner('Payment failed or cancelled. No amount charged.');
      const next = new URLSearchParams(searchParams);
      next.delete('payment');
      setSearchParams(next, { replace: true });
    }
  }, []);

  // Fetch balance
  useEffect(() => {
    if (!token) return;
    apiFetch('GET', '/api/credit/balance', token).then(res => {
      if (res?.data) setBalance(res.data.balance ?? 0);
    });
  }, [token]);

  // Listen for postMessages from iframe
  useEffect(() => {
    const handler = (event: MessageEvent) => {
      const msg = event.data;
      if (!msg || !msg.type) return;
      switch (msg.type) {
        case 'GENERATOR_READY':
          setIframeReady(true);
          break;
        case 'FLEX_OPEN_PLOT':
          // Double-click on the plot figure (Document tab) opens the Plot popup.
          setUiMode('boundary');
          setShowBoundaryFig(true);
          break;
        case 'FLEX_OPEN_HOUSE':
          // "Edit house" on the selected figure opens the House editor.
          setUiMode('house2');
          setShowHouse2(true);
          break;
        case 'FORM_CHANGED':
          if (msg.plotName && !/^Plot \d+$/i.test(msg.plotName)) setCurrentPlotName(msg.plotName);
          formDataRef.current = msg.formData || null;
          setCurrentFormData(msg.formData || null);
          break;
        case 'FORM_DATA':
          if (msg.plotName && !/^Plot \d+$/i.test(msg.plotName)) setCurrentPlotName(msg.plotName);
          formDataRef.current = msg.formData || null;
          setCurrentFormData(msg.formData || null);
          if (pendingFormResolve.current) { pendingFormResolve.current(msg.formData || null); pendingFormResolve.current = null; }
          break;
        case 'FLEX_STATE':
          setFlexLocked(!!msg.locked);
          if (msg.mode) setFlexMode(msg.mode);
          setFlexRoom(!!msg.roomOn);
          if (msg.roads) setRoads(msg.roads);
          break;
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  // Once iframe is ready, send template or load design
  useEffect(() => {
    if (!iframeReady || !iframeRef.current?.contentWindow) return;
    if (currentDesignId) {
      // Load existing design
      if (!token) return;
      apiFetch('GET', `/api/design/${currentDesignId}`, token).then(res => {
        const d = res?.data?.design || res?.data;
        if (d) {
          setCurrentPlotName(d.name);
          if (d.serviceCode) setServiceCode(d.serviceCode);
          iframeRef.current?.contentWindow?.postMessage({
            type: 'LOAD_DESIGN',
            plotData: d.plotData,
          }, '*');
        }
        setTimeout(() => setSettled(true), 350); // reveal once the design has drawn
      });
    } else {
      // Apply template from URL params with default name
      iframeRef.current.contentWindow.postMessage({
        type: 'APPLY_TEMPLATE',
        serviceCode,
        content: contentMode,
        plotName: defaultName,
      }, '*');
      // No preset to apply → the template itself is the content; reveal it.
      if (!pendingPreset) setTimeout(() => setSettled(true), 200);
    }
  }, [iframeReady]);

  // If the template picker requested a house preset, drop it straight onto the
  // Document (no popup): build the rooms from the plot's geometry, insert them,
  // and show the plot name + dimensions outside.
  useEffect(() => {
    if (!iframeReady || !pendingPreset) return;
    const irregular = serviceCode.includes('irregular');
    // 'none' = just the plot. Regular needs no change; irregular makes it a trapezoid.
    if (pendingPreset === 'none' && !irregular) { setPendingPreset(''); setSettled(true); return; }
    const t = setTimeout(() => {
      const win = iframeRef.current?.contentWindow as any;
      if (!win) return;
      if (pendingPreset === 'none') {
        // Just the plot, irregular: trapezoid boundary only, no house.
        win.postMessage({ type: 'SET_BOUNDARIES', shape: 'trapezoid', n: 30, s: 26, e: 50, w: 50, anchor: 'center' }, '*');
        win.postMessage({ type: 'SET_FIELDS', fields: { show_plot_name: true, dims_outside: true, force_rect_shape: false } }, '*');
        setPendingPreset('');
        setTimeout(() => setSettled(true), 300);
        return;
      }
      const g = win.__plotGeom;
      const { rooms, wall, fields, plot } = buildHousePreset(pendingPreset, geomFromCanvas(g?.canvasPts, g?.scale));
      // Irregular service → make the plot a trapezoid (narrower bottom edge) so it
      // reads as an irregular plot; the rectangular rooms still map into its bbox.
      // Every layout gets it: presets that fix a size use theirs, the rest use a
      // sensible default (matching the regular sample plot).
      const basePlot = plot || (irregular ? { n: 30, s: 30, e: 50, w: 50 } : null);
      // Resize the plot first so the house maps into it.
      if (basePlot) {
        const s = irregular ? Math.max(20, Math.round(basePlot.s * 0.85)) : basePlot.s;
        win.postMessage({ type: 'SET_BOUNDARIES', shape: irregular ? 'trapezoid' : 'rectangle', n: basePlot.n, s, e: basePlot.e, w: basePlot.w, anchor: 'center' }, '*');
      }
      win.postMessage({ type: 'SET_HOUSE_LAYOUT', layout: { rooms, wall, labels: true } }, '*');
      win.postMessage({ type: 'SET_FIELDS', fields: { show_plot_name: true, dims_outside: true, ...(fields || {}), ...(irregular ? { force_rect_shape: false } : {}) } }, '*');
      setPendingPreset('');
      setTimeout(() => setSettled(true), 300); // reveal once the preset has drawn
    }, 550);
    return () => clearTimeout(t);
  }, [iframeReady, pendingPreset]);

  // Keep the iframe in preview on mobile (controls form is replaced by the
  // mode switcher + full-screen Plot/House editors).
  useEffect(() => {
    if (!isMobile || !iframeReady || !iframeRef.current?.contentWindow) return;
    iframeRef.current.contentWindow.postMessage({ type: 'SWITCH_TAB', tab: mobileTab }, '*');
  }, [mobileTab, isMobile, iframeReady]);

  // Desktop drives the flex tools from the custom header, so hide the in-iframe
  // floating toolbar; on mobile keep it (the header is too small for the tools).
  useEffect(() => {
    if (!iframeReady || !iframeRef.current?.contentWindow) return;
    iframeRef.current.contentWindow.postMessage(
      { type: 'FLEX_CMD', cmd: isMobile ? 'showToolbar' : 'hideToolbar' }, '*');
  }, [iframeReady, isMobile]);

  const requestFormData = useCallback(() => {
    iframeRef.current?.contentWindow?.postMessage({ type: 'GET_FORM_DATA' }, '*');
  }, []);

  // Ask the iframe for its current form data and wait for the reply, so we save
  // the live engine state (including canvas text moves) rather than stale state.
  const getFreshFormData = useCallback((): Promise<any> => {
    return new Promise((resolve) => {
      pendingFormResolve.current = resolve;
      iframeRef.current?.contentWindow?.postMessage({ type: 'GET_FORM_DATA' }, '*');
      setTimeout(() => {
        if (pendingFormResolve.current) {
          pendingFormResolve.current = null;
          resolve(formDataRef.current);
        }
      }, 1000);
    });
  }, []);

  // Open the floor-plan editor with the plot's live layout + aspect ratio.
  const openFloorPlan = useCallback(async () => {
    const fd = await getFreshFormData();
    const feet = (f: string, i: string) => (parseFloat(fd?.[f]) || 0) + (parseFloat(fd?.[i]) || 0) / 12;
    const w = feet('n_ft', 'n_in') || feet('s_ft', 's_in');
    const h = feet('w_ft', 'w_in') || feet('e_ft', 'e_in');
    setFloorPlanAspect(w > 0 && h > 0 ? w / h : 1.35);
    setFloorPlanInitial(fd?.houseLayout?.rooms ? fd.houseLayout : { rooms: [] });
    setShowFloorPlan(true);
  }, [getFreshFormData]);

  const saveFloorPlan = useCallback((layout: FPLayout) => {
    iframeRef.current?.contentWindow?.postMessage({ type: 'SET_HOUSE_LAYOUT', layout }, '*');
    if (formDataRef.current) formDataRef.current = { ...formDataRef.current, houseLayout: layout };
    setShowFloorPlan(false);
    setUiMode('canvas');
    sendFlexCmd('setMode', { mode: 'canvas' });
  }, [sendFlexCmd]);

  // Switch the editor mode (Document / Plot / Boundary / House).
  const switchMode = useCallback((m: 'boundaries' | 'roads' | 'house' | 'canvas' | 'boundary' | 'house2') => {
    setUiMode(m);
    // popup modes keep the iframe in canvas; only the modal opens.
    sendFlexCmd('setMode', { mode: (m === 'boundary' || m === 'house2') ? 'canvas' : m });
    if (m === 'house') openFloorPlan();
    if (m === 'boundary') setShowBoundaryFig(true);
    if (m === 'house2') setShowHouse2(true);
  }, [sendFlexCmd, openFloorPlan]);
  const closeFloorPlan = useCallback(() => {
    setShowFloorPlan(false);
    setUiMode('canvas');
    sendFlexCmd('setMode', { mode: 'canvas' });
  }, [sendFlexCmd]);

  const handleSave = async (): Promise<string | null> => {
    if (!token) return null;
    setSaving(true);
    setSaveMsg('');
    // Pull the LIVE engine state (canvas moves don't fire FORM_CHANGED, and the
    // closure's currentFormData would be stale) — await the iframe's reply.
    const formData = await getFreshFormData();
    const name = currentPlotName;
    let savedId: string | null = null;
    try {
      const body: any = {
        serviceCode,
        name,
        plotData: formData ?? {},
      };
      if (currentDesignId) body.id = currentDesignId;
      const res = await apiFetch('POST', '/api/design/save', token, body);
      const saved = res?.data?.design || res?.data;
      if (saved?.id) {
        savedId = saved.id as string;
        setCurrentDesignId(savedId);
        // Persist design id in the URL so a refresh re-loads this saved
        // design instead of resetting to the sample template.
        const next = new URLSearchParams(searchParams);
        next.set('designId', savedId);
        next.delete('content');
        setSearchParams(next, { replace: true });
        setSaveMsg('Saved!');
        setTimeout(() => setSaveMsg(''), 2000);
      } else {
        console.error('[handleSave] save failed, response:', res);
        setSaveMsg('Save failed');
        setTimeout(() => setSaveMsg(''), 3000);
      }
    } catch (err) {
      console.error('[handleSave] exception:', err);
      setSaveMsg('Save error');
      setTimeout(() => setSaveMsg(''), 3000);
    }
    setSaving(false);
    return savedId;
  };

  const handleDownloadClick = async () => {
    if (!token) return;
    setDownloadError(null);

    // Auto-save first if no designId
    let designId = currentDesignId;
    if (!designId) {
      designId = await handleSave();
      await new Promise(r => setTimeout(r, 300));
    }
    if (!designId) return;

    // Fetch design (to check downloadCount) + service pricing in parallel
    const [designRes, svcRes] = await Promise.all([
      apiFetch('GET', `/api/design/${designId}`, token),
      apiFetch('GET', '/api/service/list', token),
    ]);

    const design = designRes?.data?.design || designRes?.data;
    const services: any[] = svcRes?.data || [];
    const svc = services.find((s: any) => s.code === serviceCode);

    const reDownload = (design?.downloadCount ?? 0) > 0;
    const cost = reDownload ? (svc?.tier2Price ?? 0) : (svc?.tier1Price ?? 0);

    setIsReDownload(reDownload);
    setDownloadCost(cost);
    setShowDownloadDialog(true);
  };

  const handleConfirmDownload = async () => {
    if (!token || !currentDesignId) return;
    setDownloading(true);
    setShowDownloadDialog(false);
    setDownloadError(null);

    try {
      // Deduct credits + get token
      const res = await apiFetch('POST', '/api/download/token', token, { designId: currentDesignId });
      if (!res?.data?.tokenId) {
        const msg = res?.message || 'Insufficient balance or download failed.';
        setDownloadError(msg);
        setDownloading(false);
        return;
      }

      const tokenId = res.data.tokenId;

      // Trigger PDF in iframe
      iframeRef.current?.contentWindow?.postMessage({ type: 'TRIGGER_DOWNLOAD' }, '*');

      // Confirm download record
      await apiFetch('POST', '/api/download/confirm', token, { tokenId });

      // Refresh balance
      const balRes = await apiFetch('GET', '/api/credit/balance', token);
      if (balRes?.data) setBalance(balRes.data.balance ?? 0);
    } catch (err: any) {
      setDownloadError(err.message || 'Download failed. Please try again.');
    }

    setDownloading(false);
  };

  if (!user) return null;

  const serviceName = SERVICE_NAMES[serviceCode] || serviceCode;
  const TAB_BAR_HEIGHT = 56;

  return (
    <div style={{ position: 'fixed', inset: 0, display: 'flex', flexDirection: 'column', background: '#242424' }}>
      <SEO title={`${serviceName} (Flex) - RRD Planners`} description="Plot map generator" />

      {/* Toolbar */}
      <div style={{
        flexShrink: 0,
        background: '#0d0d0d', borderBottom: '1px solid #2a2a2a',
        display: 'flex', alignItems: 'center', gap: isMobile ? 6 : 12,
        padding: isMobile ? '0 8px' : '0 16px', height: isMobile ? 44 : 52,
        zIndex: 100,
      }}>
        <button
          onClick={() => navigate(route('/dashboard'))}
          style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, padding: '6px 8px', borderRadius: 6, flexShrink: 0 }}
        >
          <ArrowLeft size={16} />
          {!isMobile && <span style={{ fontSize: 13 }}>Back</span>}
        </button>

        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <span style={{ color: '#FFD93D', fontWeight: 700, fontSize: isMobile ? 13 : 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{serviceName}</span>
          {!isMobile && (
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
              <span style={{ color: '#999', fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 220 }}>/ {currentPlotName}</span>
              <button title="Rename plot" onClick={() => { setNameDraft(currentPlotName); setEditingName(true); }}
                style={{ display: 'flex', alignItems: 'center', background: 'none', border: 'none', color: '#888', cursor: 'pointer', padding: 2 }}>
                <Pencil size={12} />
              </button>
              {editingName && (
                <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: 6, zIndex: 250, background: '#161616', border: '1px solid #2a2a2a', borderRadius: 8, padding: 8, boxShadow: '0 8px 24px rgba(0,0,0,.5)', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input autoFocus value={nameDraft} onChange={(e) => setNameDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') saveName(nameDraft); if (e.key === 'Escape') setEditingName(false); }}
                    placeholder="Plot name"
                    style={{ width: 220, background: '#1a1a1a', border: '1px solid #333', color: '#fff', borderRadius: 6, padding: '7px 9px', fontSize: 13, outline: 'none' }} />
                  <button onClick={() => saveName(nameDraft)} title="Save" style={{ display: 'flex', alignItems: 'center', background: '#FFD93D', color: '#1a1a1a', border: 'none', borderRadius: 6, padding: 7, cursor: 'pointer' }}><Check size={14} /></button>
                  <button onClick={() => setEditingName(false)} title="Cancel" style={{ display: 'flex', alignItems: 'center', background: 'transparent', color: '#aaa', border: '1px solid #333', borderRadius: 6, padding: 7, cursor: 'pointer' }}><XIcon size={14} /></button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Custom flex tools (desktop) — 3 modes drive the iframe via postMessage */}
        {!isMobile && iframeReady && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, marginRight: 4 }}>
            {/* mode segmented control */}
            <div style={{ display: 'flex', gap: 2, background: '#1a1a1a', borderRadius: 9, padding: 3, border: '1px solid #2a2a2a' }}>
              {([['canvas', 'Document'], ['boundary', 'Plot'], ['house2', 'House']] as const).map(([k, lbl]) => {
                const on = uiMode === k;
                return (
                  <button key={k} onClick={() => switchMode(k)} disabled={flexLocked}
                    style={{
                      background: on ? '#FFD93D' : 'transparent', color: on ? '#1a1a1a' : '#bbb',
                      border: 'none', borderRadius: 7, padding: '6px 11px', fontSize: 12, fontWeight: 700,
                      cursor: flexLocked ? 'not-allowed' : 'pointer', opacity: flexLocked ? 0.5 : 1, whiteSpace: 'nowrap',
                    }}>{lbl}</button>
                );
              })}
            </div>

            <div style={{ width: 1, height: 20, background: '#2a2a2a', margin: '0 2px' }} />
            <FxBtn compact title="Undo (⌘/Ctrl+Z)" onClick={() => sendMsg('UNDO')}><Undo2 size={14} /></FxBtn>
            <FxBtn compact title="Redo (⌘/Ctrl+Shift+Z)" onClick={() => sendMsg('REDO')}><Redo2 size={14} /></FxBtn>
            <div style={{ width: 1, height: 20, background: '#2a2a2a', margin: '0 2px' }} />
            <FxBtn active={flexLocked} title={flexLocked ? 'Locked — click to edit' : 'Lock the layout'} onClick={() => sendFlexCmd('toggleLock')}>
              {flexLocked ? <Lock size={14} /> : <Unlock size={14} />}
            </FxBtn>
            <FxBtn compact title="Zoom out" onClick={() => { setFitted(false); sendFlexCmd('zoomOut'); }}><ZoomOut size={14} /></FxBtn>
            <FxBtn compact title="Zoom in" onClick={() => { setFitted(false); sendFlexCmd('zoomIn'); }}><ZoomIn size={14} /></FxBtn>
            <FxBtn active={fitted} compact title={fitted ? 'Back to normal view' : 'Fit to screen'} onClick={() => { const f = !fitted; setFitted(f); sendFlexCmd(f ? 'fit' : 'normal'); }}><Maximize2 size={14} /></FxBtn>
          </div>
        )}

        {/* Credit balance */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#FFD93D', fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
          <Coins size={14} />
          <span>{balance ?? '...'}</span>
        </div>

        {/* Desktop-only: Save button */}
        {!isMobile && (
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: saving ? '#333' : '#2a2a2a',
              border: '1px solid #444', borderRadius: 6,
              color: saveMsg ? '#4CAF50' : '#ddd',
              fontSize: 12, fontWeight: 600, padding: '6px 14px', cursor: 'pointer',
            }}
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {saveMsg || 'Save'}
          </button>
        )}

        {/* Desktop-only: Download PDF button */}
        {!isMobile && (
          <button
            onClick={handleDownloadClick}
            disabled={downloading}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: '#FFD93D', color: '#1a1a1a',
              border: 'none', borderRadius: 6,
              fontSize: 12, fontWeight: 700, padding: '6px 14px', cursor: 'pointer',
            }}
          >
            {downloading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
            Download PDF
          </button>
        )}
      </div>

      {/* Mobile mode switcher — Document / Plot / House (desktop shows these in the toolbar) */}
      {isMobile && iframeReady && (
        <div style={{ flexShrink: 0, display: 'flex', gap: 6, padding: '6px 8px', background: '#0d0d0d', borderBottom: '1px solid #2a2a2a', zIndex: 90 }}>
          {([['canvas', 'Document'], ['boundary', 'Plot'], ['house2', 'House']] as const).map(([k, lbl]) => {
            const on = uiMode === k;
            return (
              <button key={k} onClick={() => switchMode(k)} disabled={flexLocked}
                style={{
                  flex: 1, background: on ? '#FFD93D' : '#1a1a1a', color: on ? '#1a1a1a' : '#bbb',
                  border: '1px solid #2a2a2a', borderRadius: 8, padding: '9px 6px', fontSize: 12, fontWeight: 700,
                  cursor: flexLocked ? 'not-allowed' : 'pointer', opacity: flexLocked ? 0.5 : 1,
                }}>{lbl}</button>
            );
          })}
        </div>
      )}

      {/* Iframe — fills remaining height, minus mobile tab bar */}
      <div style={{ flex: 1, overflow: 'hidden', position: 'relative', marginBottom: isMobile ? TAB_BAR_HEIGHT : 0 }}>
        {(!iframeReady || !settled) && (
          <div style={{ position: 'absolute', inset: 0, zIndex: 5, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#1a1a1a' }}>
            <Loader2 size={32} className="animate-spin" style={{ color: '#FFD93D' }} />
          </div>
        )}
        <iframe
          ref={iframeRef}
          src={`/legacy/generator-embed-flex.html?v=${IFRAME_CACHE_KEY}`}
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
            display: iframeReady ? 'block' : 'none',
          }}
          title="Plot Generator"
        />
      </div>

      {/* Mobile Bottom Tab Bar */}
      {isMobile && (
        <div style={{
          position: 'fixed', bottom: 0, left: 0, right: 0,
          height: TAB_BAR_HEIGHT,
          background: '#0d0d0d', borderTop: '1px solid #333',
          display: 'flex', zIndex: 100,
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        }}>
          {/* Save tab */}
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              gap: 3, background: 'none', border: 'none',
              color: saveMsg ? '#4CAF50' : '#666',
              fontSize: 10, fontWeight: 600, cursor: 'pointer',
              opacity: saving ? 0.5 : 1,
            }}
          >
            {saving ? <Loader2 size={20} className="animate-spin" /> : <Save size={20} />}
            {saveMsg || 'Save'}
          </button>

          {/* Download PDF tab */}
          <button
            onClick={handleDownloadClick}
            disabled={downloading}
            style={{
              flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              gap: 3, background: 'none', border: 'none',
              color: '#FFD93D',
              fontSize: 10, fontWeight: 700, cursor: 'pointer',
              opacity: downloading ? 0.5 : 1,
            }}
          >
            {downloading ? <Loader2 size={20} className="animate-spin" /> : <Download size={20} />}
            PDF
          </button>
        </div>
      )}

      {/* House editor on the actual figure (plot shown faded for reference) */}
      <House2Editor
        open={showHouse2}
        getIframe={() => iframeRef.current}
        onClose={() => { setShowHouse2(false); setUiMode('canvas'); sendFlexCmd('setMode', { mode: 'canvas' }); }}
      />

      {/* Plot editor on the actual figure */}
      <BoundaryFigureEditor
        open={showBoundaryFig}
        getIframe={() => iframeRef.current}
        onClose={() => { setShowBoundaryFig(false); setUiMode('canvas'); sendFlexCmd('setMode', { mode: 'canvas' }); }}
      />

      {/* Floor-plan (multi-room house) editor */}
      <FloorPlanEditor
        open={showFloorPlan}
        initialLayout={floorPlanInitial}
        aspect={floorPlanAspect}
        plotName={currentPlotName}
        onCancel={closeFloorPlan}
        onSave={saveFloorPlan}
      />

      {/* Download Confirmation Dialog */}
      {showDownloadDialog && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 200,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
        }}>
          <div style={{
            background: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: 16,
            padding: isMobile ? 20 : 28, maxWidth: 380, width: '100%',
          }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
              <div style={{ width: 44, height: 44, background: 'rgba(255,217,61,0.1)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Download size={20} style={{ color: '#FFD93D' }} />
              </div>
              <div>
                <div style={{ color: '#fff', fontSize: 16, fontWeight: 700 }}>Download PDF</div>
                <div style={{ color: '#666', fontSize: 12 }}>{isReDownload ? 'Re-download (edit rate applies)' : 'First download'}</div>
              </div>
            </div>

            {/* Cost breakdown */}
            <div style={{ background: '#111', borderRadius: 10, padding: '14px 16px', marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                <span style={{ color: '#888', fontSize: 13 }}>{isReDownload ? 'Edit rate' : 'New plan rate'}</span>
                <span style={{ color: '#FFD93D', fontSize: 15, fontWeight: 700 }}>₹{downloadCost}</span>
              </div>
              <div style={{ height: 1, background: '#222', marginBottom: 10 }} />
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#888', fontSize: 13 }}>Your balance</span>
                <span style={{ color: (balance ?? 0) >= downloadCost ? '#aaa' : '#ef4444', fontSize: 13, fontWeight: 600 }}>
                  ₹{balance ?? 0}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
                <span style={{ color: '#888', fontSize: 13 }}>After deduction</span>
                <span style={{ color: '#aaa', fontSize: 13 }}>₹{Math.max(0, (balance ?? 0) - downloadCost)}</span>
              </div>
            </div>

            {/* Insufficient balance — pay directly */}
            {(balance ?? 0) < downloadCost && (
              <div style={{ background: 'rgba(255,217,61,0.06)', border: '1px solid rgba(255,217,61,0.2)', borderRadius: 8, padding: '12px 14px', marginBottom: 16 }}>
                <p style={{ color: '#FFD93D', fontSize: 13, fontWeight: 600, margin: '0 0 6px' }}>
                  Insufficient balance — ₹{downloadCost - (balance ?? 0)} more needed
                </p>
                <p style={{ color: '#888', fontSize: 12, margin: '0 0 10px' }}>
                  Pay now and credits will be added instantly to your wallet.
                </p>
                <button
                  onClick={async () => {
                    const needed = downloadCost - (balance ?? 0);
                    try {
                      const returnUrl = window.location.pathname + window.location.search;
                      const res = await apiFetch('POST', '/api/payment/create-order', token!, { customAmount: needed, returnUrl });
                      const data = res?.data;
                      if (data?.payuFormData && data?.payuBaseUrl) {
                        const form = document.createElement('form');
                        form.method = 'POST';
                        form.action = data.payuBaseUrl;
                        for (const [key, value] of Object.entries(data.payuFormData)) {
                          const input = document.createElement('input');
                          input.type = 'hidden';
                          input.name = key;
                          input.value = String(value);
                          form.appendChild(input);
                        }
                        document.body.appendChild(form);
                        form.submit();
                      }
                    } catch { }
                  }}
                  style={{
                    width: '100%', padding: '10px 0', borderRadius: 8, border: 'none',
                    background: '#FFD93D', color: '#1a1a1a', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                  }}
                >
                  Pay ₹{downloadCost - (balance ?? 0)} Now
                </button>
              </div>
            )}

            {/* Buttons */}
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => setShowDownloadDialog(false)}
                style={{
                  flex: 1, padding: '11px 0', borderRadius: 8, border: '1px solid #333',
                  background: 'transparent', color: '#888', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDownload}
                disabled={(balance ?? 0) < downloadCost}
                style={{
                  flex: 1, padding: '11px 0', borderRadius: 8, border: 'none',
                  background: (balance ?? 0) < downloadCost ? '#555' : '#FFD93D',
                  color: '#1a1a1a', cursor: (balance ?? 0) < downloadCost ? 'not-allowed' : 'pointer',
                  fontSize: 13, fontWeight: 700,
                }}
              >
                Confirm & Download
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment return banner */}
      {paymentBanner && (
        <div style={{
          position: 'fixed', top: 60, left: '50%', transform: 'translateX(-50%)',
          background: paymentBanner.includes('success') ? 'rgba(34,197,94,0.95)' : 'rgba(239,68,68,0.95)',
          color: '#fff', borderRadius: 10, padding: '12px 20px', zIndex: 300, fontSize: 13,
          fontWeight: 600, maxWidth: 360, display: 'flex', alignItems: 'center', gap: 8,
        }}>
          {paymentBanner}
          <button onClick={() => setPaymentBanner(null)} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', marginLeft: 8, fontSize: 16 }}>✕</button>
        </div>
      )}

      {/* Download error toast */}
      {downloadError && (
        <div style={{
          position: 'fixed', bottom: isMobile ? TAB_BAR_HEIGHT + 12 : 24, left: '50%', transform: 'translateX(-50%)',
          background: '#1e1e1e', border: '1px solid #ef4444', borderRadius: 10,
          padding: '12px 20px', zIndex: 300, color: '#ef4444', fontSize: 13, maxWidth: 360,
        }}>
          {downloadError}{' '}
          <button onClick={() => setDownloadError(null)} style={{ background: 'none', border: 'none', color: '#666', cursor: 'pointer', marginLeft: 8 }}>✕</button>
        </div>
      )}

      {/* Save success toast */}
      {saveMsg && isMobile && (
        <div style={{
          position: 'fixed', bottom: TAB_BAR_HEIGHT + 12, left: '50%', transform: 'translateX(-50%)',
          background: '#1e1e1e', border: '1px solid #4CAF50', borderRadius: 10,
          padding: '10px 20px', zIndex: 300, color: '#4CAF50', fontSize: 13,
        }}>
          {saveMsg}
        </div>
      )}
    </div>
  );
};

export default FlexPage;
